/**
 * Typed API failure. Every fetch wrapper in services/ throws this so callers
 * (and the QueryClient retry policy) can branch on the HTTP status instead of
 * pattern-matching message text.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** True for 401/403 — the session is gone or the caller is not allowed. Never retry these. */
export function isAuthError(error: unknown): boolean {
  if (error instanceof ApiError) return error.status === 401 || error.status === 403;
  if (error && typeof error === 'object' && 'status' in error) {
    const status = Number((error as { status?: unknown }).status);
    return status === 401 || status === 403;
  }
  if (error instanceof Error) {
    return /\b(401|403)\b|UNAUTHORIZED|FORBIDDEN|missing authentication token/i.test(error.message);
  }
  return false;
}

/** True when an error body is an HTML page (nginx/Vite proxy or server error page), not an API payload. */
export function isHtmlErrorBody(body: string, contentType?: string | null): boolean {
  if (contentType && /\btext\/html\b/i.test(contentType)) return true;
  return /^\s*(<!doctype html|<html[\s>]|<head[\s>]|<body[\s>])/i.test(body);
}

/** Human-readable message for an HTTP status when the response has no usable API error. */
export function httpStatusMessage(status: number): string {
  switch (status) {
    case 400:
      return 'The request was rejected (HTTP 400).';
    case 401:
      return 'Your session has expired — sign in again (HTTP 401).';
    case 403:
      return "You don't have access to this resource (HTTP 403).";
    case 404:
      return 'The requested resource was not found (HTTP 404).';
    case 408:
    case 504:
      return `The server took too long to respond (HTTP ${status}). Try again.`;
    case 413:
      return 'The request is too large (HTTP 413).';
    case 429:
      return 'Too many requests — wait a moment and try again (HTTP 429).';
    case 502:
      return 'The server is unreachable right now (HTTP 502). Try again shortly.';
    case 503:
      return 'The service is temporarily unavailable (HTTP 503). Try again shortly.';
    default:
      if (status >= 500) return `The server hit an error (HTTP ${status}).`;
      return `Request failed (HTTP ${status}).`;
  }
}
